import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, DragDropModule, CdkDragStart, CdkDragMove } from '@angular/cdk/drag-drop';

export interface DraggableListItem {
  [key: string]: any;
}

export interface DragEvent<T> {
  item: T;
  event: any;
}

// Special identifier for new items
const NEW_ITEM_ID = '__NEW_ITEM__';

@Component({
  selector: 'app-draggable-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    FormsModule,
    DragDropModule
  ],
  templateUrl: './draggable-list.component.html',
  styleUrl: './draggable-list.component.scss'
})
export class DraggableListComponent<T extends DraggableListItem> implements OnChanges {
  @Input() items: T[] = [];
  @Input() displayProperty: string = 'name';
  @Input() showEdit: boolean = true;
  @Input() showDelete: boolean = true;
  @Input() listClass: string = '';
  @Input() itemClass: string = '';

  @Output() itemsReordered = new EventEmitter<T[]>();
  @Output() editItem = new EventEmitter<T>();
  @Output() deleteItem = new EventEmitter<T>();
  @Output() itemSave = new EventEmitter<{item: T, newValue: string}>();
  @Output() itemAdd = new EventEmitter<{[key: string]: any}>();
  @Output() dragStarted = new EventEmitter<DragEvent<T>>();
  @Output() dragMoved = new EventEmitter<DragEvent<T>>();
  @Output() dragEnded = new EventEmitter<DragEvent<T>>();

  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;
  @ViewChild('addInput') addInput!: ElementRef<HTMLInputElement>;

  isDragging = false;

  /** Tooltip shown on the delete control of a protected (system-reserved) item. */
  readonly protectedDeleteTooltip = 'This entry is protected and cannot be deleted';

  // Edit state management
  editingItemId: any = null;
  editingValue: string = '';

  // Add new item state
  isAddingNewItem: boolean = false;
  newItemValue: string = '';

  // Internal items array that includes the new item when adding
  displayedItems: T[] = [];

  constructor() {
    this.updateDisplayedItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.updateDisplayedItems();
    }
  }

  // Update the displayed items array
  private updateDisplayedItems(): void {
    if (this.isAddingNewItem) {
      // Add a temporary new item at the beginning
      const newItem = { [this.displayProperty]: '', [NEW_ITEM_ID]: true } as unknown as T;
      this.displayedItems = [newItem, ...this.items];
    } else {
      this.displayedItems = [...this.items];
    }
  }

  drop(event: CdkDragDrop<T[]>) {
    const updatedItems = [...this.items];
    moveItemInArray(updatedItems, event.previousIndex, event.currentIndex);
    this.itemsReordered.emit(updatedItems);
  }

  onDragStarted(event: CdkDragStart<T>) {
    this.isDragging = true;

    // Apply visual feedback for drag start
    const dragElement = event.source.element.nativeElement;
    dragElement.style.transform = 'scale(1.05)';

    this.dragStarted.emit({ item: event.source.data, event });
  }

  onDragMoved(event: CdkDragMove<T>) {
    this.isDragging = true;
    this.dragMoved.emit({ item: event.source.data, event });
  }

  onDragEnded(event: any) {
    this.isDragging = false;

    // Reset visual modifications
    const dragElement = event.source.element.nativeElement;
    dragElement.style.transform = '';

    this.dragEnded.emit({ item: event.source.data, event });
  }

  onEditClick(item: T): void {
    if (this.isDragging) return; // Don't edit while dragging

    this.cancelAdd(); // Cancel any add operation
    this.startEdit(item);
  }

  onDeleteClick(item: T): void {
    if (this.isDeleteProtected(item)) return; // system-reserved items cannot be deleted
    this.cancelEdit();
    this.cancelAdd();
    this.deleteItem.emit(item);
  }

  // A system-reserved item is referenced by program logic and must not be deleted.
  isDeleteProtected(item: T): boolean {
    return Boolean((item as any)?.systemReserved);
  }

  startEdit(item: T): void {
    this.editingItemId = this.getItemId(item);
    this.editingValue = this.getDisplayValue(item);

    // Focus the input after the view updates with proper error handling
    setTimeout(() => {
      try {
        if (this.editInput && this.editInput.nativeElement) {
          this.editInput.nativeElement.focus();
          this.editInput.nativeElement.select();
        }
      } catch (error) {
        console.warn('Could not focus edit input:', error);
      }
    }, 100); // Increased timeout to ensure DOM is updated
  }

  saveEdit(item: T): void {
    if (this.editingValue.trim() && this.editingValue.trim() !== this.getDisplayValue(item)) {
      this.itemSave.emit({
        item,
        newValue: this.editingValue.trim()
      });
    }
    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editingItemId = null;
    this.editingValue = '';
  }

  isEditing(item: T): boolean {
    return this.editingItemId === this.getItemId(item);
  }

  private getItemId(item: T): any {
    // Use a unique identifier if available, otherwise use the display property value, otherwise use the item reference
    return (item as any).id || this.getDisplayValue(item) || item;
  }

  onKeyDown(event: KeyboardEvent, item: T): void {
    if (event.key === 'Enter') {
      this.saveEdit(item);
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  onEditInputClick(event: Event): void {
    event.stopPropagation(); // Prevent drag events when clicking on input
  }

  getDisplayValue(item: T): string {
    return item[this.displayProperty] || '';
  }

  // Add new item methods - public to be called from parent
  public startAdd(): void {
    this.cancelEdit();
    this.isAddingNewItem = true;
    this.newItemValue = '';

    // Update the displayed items first
    this.updateDisplayedItems();

    // Focus the input after the view updates with proper error handling
    setTimeout(() => {
      try {
        if (this.addInput && this.addInput.nativeElement) {
          this.addInput.nativeElement.focus();
        }
      } catch (error) {
        console.warn('Could not focus add input:', error);
      }
    }, 0);
  }

  saveAdd(): void {
    if (this.newItemValue.trim()) {
      const newItem: {[key: string]: any} = {
        [this.displayProperty]: this.newItemValue.trim()
      };
      this.itemAdd.emit(newItem);
    }
    this.cancelAdd();
  }

  cancelAdd(): void {
    this.isAddingNewItem = false;
    this.newItemValue = '';
    this.updateDisplayedItems();
  }

  onAddKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.saveAdd();
    } else if (event.key === 'Escape') {
      this.cancelAdd();
    }
  }

  isNewItem(item: T): boolean {
    return (item as any)[NEW_ITEM_ID] === true;
  }

  isAddingThisItem(item: T): boolean {
    return this.isNewItem(item) && this.isAddingNewItem;
  }

  onAddInputClick(event: Event): void {
    event.stopPropagation(); // Prevent drag events when clicking on input
  }
}
